import { useEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { useRubriques } from "@/hooks/useRubriques";
import { useParametres } from "@/hooks/useParametres";
import { RESEAUX_SOCIAUX } from "./SocialLinks";
import { setJsonLd, SITE_URL, SITE_NAME } from "@/lib/seo";
import type { RubriqueDto } from "@/types/rubrique.types";

/**
 * Schémas Schema.org valables sur tout le site public : WebSite (avec
 * SearchAction pour la boîte de recherche Google), Organization et
 * SiteNavigationElement. Montés une seule fois dans PublicLayout — ils ne se
 * démontent pas entre les pages publiques (routes imbriquées sous le layout).
 */
export function SeoGlobal() {
  const { locale } = useI18n();
  const { data: rubriquesData } = useRubriques();
  const { data: parametres } = useParametres();

  useEffect(() => {
    setJsonLd("ld-website", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: SITE_URL,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    });
  }, []);

  useEffect(() => {
    const sameAs = RESEAUX_SOCIAUX
      .map((r) => parametres?.[r.cle])
      .filter((url): url is string => !!url);

    setJsonLd("ld-organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo_Ramba.png`,
      ...(sameAs.length > 0 ? { sameAs } : {}),
    });
  }, [parametres]);

  useEffect(() => {
    const rubriques: RubriqueDto[] = (Array.isArray(rubriquesData) ? rubriquesData : [])
      .filter((r) => r.actif)
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

    const items = [
      { name: locale === "fr" ? "Accueil" : "Home", url: `${SITE_URL}/` },
      { name: "Fact-check", url: `${SITE_URL}/factcheck` },
      ...rubriques.map((r) => ({
        name: locale === "fr" ? r.nom : (r.nomEn || r.nom),
        url: `${SITE_URL}/rubrique/${r.slug}`,
      })),
      { name: locale === "fr" ? "À propos" : "About", url: `${SITE_URL}/about` },
    ];

    setJsonLd("ld-site-navigation", {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items.map((item, i) => ({
        "@type": "SiteNavigationElement",
        position: i + 1,
        name: item.name,
        url: item.url,
      })),
    });
  }, [rubriquesData, locale]);

  return null;
}
